import React from 'react';
import Logo from '../../components/Layout/Logo';
import './SecurityPolicyPage.css';

// Bump this whenever the policy content actually changes. The canonical text is
// docs/cra/04-SECURITY-POLICY.md in the YoctoSpaOS repo; keep the two in sync.
const LAST_UPDATED_DATE = 'October 6, 2026';
const SECURITY_EMAIL = 'security@arcticspas.com';

const SecurityPolicyPage: React.FC = () => {
  return (
    <div className="security-policy-page">
      <div className="security-container">
        <header className="security-header">
          <div className="security-logo">
            <Logo height="4rem" />
          </div>
          <h1>Vulnerability Disclosure Policy</h1>
          <p className="last-updated">Last Updated: {LAST_UPDATED_DATE}</p>
        </header>

        <section>
          <p>
            Arctic Spas takes the security of its connected hot tub controllers, mobile apps and
            cloud services seriously. We welcome reports from security researchers, dealers and
            customers, and we commit to working with you to understand and fix the issue.
          </p>
          <div className="highlight">
            <p>
              <strong>Found a security issue?</strong> E-mail{' '}
              <a href={`mailto:${SECURITY_EMAIL}`}>{SECURITY_EMAIL}</a>. We acknowledge every
              report within 3 business days.
            </p>
          </div>
        </section>

        <section>
          <h2>1. Scope</h2>
          <p>This policy covers:</p>
          <ul>
            <li>Hot tub controller firmware (YoctoSpaOS) and the topside touchscreen software</li>
            <li>Arctic Spas and COLDTUB&trade; mobile apps</li>
            <li>
              Cloud services used by the products (dealer and customer portals, dealer API, AWS IoT
              integration, firmware update distribution)
            </li>
          </ul>
          <p>Out of scope:</p>
          <ul>
            <li>Physical attacks that require disassembly of the equipment bay</li>
            <li>Denial of service by flooding a customer&apos;s own network</li>
            <li>Social engineering of Arctic Spas staff or dealers</li>
            <li>
              Issues in third-party services we do not operate (please report those to the vendor)
            </li>
          </ul>
        </section>

        <section>
          <h2>2. How to Report</h2>
          <p>
            Send an e-mail to <a href={`mailto:${SECURITY_EMAIL}`}>{SECURITY_EMAIL}</a> with:
          </p>
          <ul>
            <li>
              The product and firmware version. On a spa it is shown at{' '}
              <code>http://&lt;spa-ip&gt;/device-info</code> on the spa&apos;s own web page, or in
              the app.
            </li>
            <li>A description of the issue and its impact</li>
            <li>Steps to reproduce, with any scripts or screenshots that help</li>
            <li>Your contact details if you would like updates or credit</li>
          </ul>
          <p>
            Please do not include customer data you may have seen; tell us that you saw it and we
            will take it from there. A machine-readable version of this contact information is
            published at <code>/.well-known/security.txt</code>.
          </p>
        </section>

        <section>
          <h2>3. What We Commit To</h2>
          <ul>
            <li>Acknowledge your report within <strong>3 business days</strong>.</li>
            <li>
              Give an initial assessment (severity, whether we can reproduce it) within{' '}
              <strong>10 business days</strong>.
            </li>
            <li>Keep you informed of progress and tell you when a fix ships.</li>
            <li>
              Fix confirmed vulnerabilities according to severity: Critical within 14 days, High
              within 45 days, Medium within 90 days.
            </li>
            <li>Credit you in the release notes if you wish.</li>
          </ul>
        </section>

        <section>
          <h2>4. Coordinated Disclosure</h2>
          <p>
            We ask reporters to give us <strong>90 days</strong> from acknowledgment before public
            disclosure, or until a fix is available to customers if that is sooner. We will
            coordinate the publication date with you and publish an advisory for Critical and High
            issues.
          </p>
        </section>

        <section>
          <h2>5. Safe Harbour</h2>
          <p>
            We will not pursue legal action against researchers who act in good faith: test only
            devices, accounts and data they own or have permission to test, avoid privacy
            violations and service disruption, and give us reasonable time to fix an issue before
            disclosing it.
          </p>
        </section>

        <section>
          <h2>6. Security Updates</h2>
          <p>
            Security fixes are delivered free of charge over the air to all supported controllers
            and are listed under <strong>Security</strong> in the release notes. The current
            firmware version and the support contact are shown on the spa at{' '}
            <code>http://&lt;spa-ip&gt;/device-info</code> and in the app.
          </p>
        </section>

        <section>
          <h2>7. Contact</h2>
          <div className="contact-info">
            <p>
              <strong>Arctic Spas &mdash; Product Security</strong>
              <br />
              Email: <a href={`mailto:${SECURITY_EMAIL}`}>{SECURITY_EMAIL}</a>
            </p>
            <p>
              For privacy questions that are not security vulnerabilities, see our{' '}
              <a href="/app-privacy">Privacy Policy</a>.
            </p>
          </div>
        </section>

        <footer className="security-footer">
          <p>&copy; {new Date().getFullYear()} Arctic Spas. All rights reserved.</p>
        </footer>
      </div>
    </div>
  );
};

export default SecurityPolicyPage;
